import { useEffect, useRef, useState } from "react";
import { X, Check, Move, ZoomIn } from "lucide-react";

const OUT_W = 1080, OUT_H = 1920;      // portrait 9:16 output
const R = OUT_W / OUT_H;               // target width/height ratio (0.5625)
const MAX_ZOOM = 3;

// Show the (landscape) source image full-size and let the user DRAG a fixed 9:16 portrait
// box over it to choose the crop. A zoom slider shrinks the box (tighter crop). Confirm
// renders a crisp 1080x1920 portrait JPEG.
export default function PortraitCropModal({ image, onCancel, onConfirm }) {
  const imgRef = useRef(null);
  const [disp, setDisp] = useState({ w: 0, h: 0 });
  const [base, setBase] = useState({ w: 0, h: 0 });   // box size at zoom = 1 (largest 9:16 fit)
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);
  const drag = useRef(null);

  const boxOf = (z) => ({ w: base.w / z, h: base.h / z });
  const box = boxOf(zoom);

  const measure = () => {
    const el = imgRef.current;
    if (!el || !el.clientWidth) return;
    const w = el.clientWidth, h = el.clientHeight;
    let bw, bh;
    if (w / h > R) { bh = h; bw = h * R; } else { bw = w; bh = w / R; }
    setDisp({ w, h });
    setBase({ w: bw, h: bh });
    setZoom(1);
    setPos({ x: (w - bw) / 2, y: (h - bh) / 2 });
    setReady(true);
  };

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []); // eslint-disable-line

  const clamp = (x, y, b) => ({
    x: Math.min(Math.max(0, x), disp.w - b.w),
    y: Math.min(Math.max(0, y), disp.h - b.h),
  });

  const onZoom = (z) => {
    const oldB = boxOf(zoom), newB = boxOf(z);
    // keep the box centre stable while zooming
    const cx = pos.x + oldB.w / 2, cy = pos.y + oldB.h / 2;
    setZoom(z);
    setPos(clamp(cx - newB.w / 2, cy - newB.h / 2, newB));
  };

  const start = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    setPos(clamp(drag.current.px + (e.clientX - drag.current.sx), drag.current.py + (e.clientY - drag.current.sy), box));
  };
  const end = () => { drag.current = null; };

  const confirm = () => {
    const el = imgRef.current;
    if (!el || !disp.w) return;
    const scale = el.naturalWidth / disp.w;   // display px -> natural px
    const sx = pos.x * scale, sy = pos.y * scale, sw = box.w * scale, sh = box.h * scale;
    const c = document.createElement("canvas");
    c.width = OUT_W; c.height = OUT_H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, OUT_W, OUT_H);
    ctx.drawImage(el, sx, sy, sw, sh, 0, 0, OUT_W, OUT_H);
    onConfirm(c.toDataURL("image/jpeg", 0.92));
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={onCancel} data-testid="portrait-crop-overlay">
      <div onClick={(e) => e.stopPropagation()} className="sms-tweed w-full max-w-2xl rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] text-white p-5 sms-fade-up" style={{ boxShadow: "0 30px 90px -24px rgba(0,0,0,.85)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg flex items-center gap-2"><Move size={18} className="text-[#8fbf82]" /> Pick your portrait crop</div>
          <button onClick={onCancel} data-testid="portrait-crop-close" className="text-white/50 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div className="flex justify-center">
          <div className="relative inline-block select-none touch-none leading-none overflow-hidden rounded-xl">
            <img
              ref={imgRef}
              src={image}
              alt="crop source"
              onLoad={measure}
              draggable={false}
              className="block"
              style={{ maxHeight: "60vh", maxWidth: "100%" }}
            />
            {ready && (
              <div
                data-testid="portrait-crop-box"
                onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
                className="absolute cursor-move rounded-lg"
                style={{
                  left: pos.x, top: pos.y, width: box.w, height: box.h,
                  boxShadow: "0 0 0 9999px rgba(0,0,0,.6)",
                  border: "2px solid #ffd21e",
                }}
              >
                {/* rule-of-thirds guides */}
                <div className="pointer-events-none absolute inset-0">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/25" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/25" />
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white/25" />
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white/25" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Zoom slider — shrinks the crop box for a tighter, more zoomed-in portrait */}
        <div className="flex items-center gap-3 mt-4">
          <ZoomIn size={16} className="text-[#8fbf82] shrink-0" />
          <input
            data-testid="portrait-crop-zoom"
            type="range" min={1} max={MAX_ZOOM} step={0.01} value={zoom}
            onChange={(e) => onZoom(parseFloat(e.target.value))}
            className="w-full accent-[#ffd21e]"
          />
          <span className="text-xs font-extrabold tabular-nums text-white/70 w-10 text-right">{zoom.toFixed(1)}×</span>
        </div>

        <p className="text-[11px] text-white/45 mt-2 text-center">Drag the yellow box across the picture, and slide to zoom in on the part you want. Output is a crisp 1080×1920 portrait.</p>
        <button data-testid="portrait-crop-confirm" onClick={confirm} className="mt-4 w-full flex items-center justify-center gap-2 font-extrabold py-3 rounded-xl text-white bg-[#dc2626] hover:bg-[#b91c1c] transition-colors">
          <Check size={18} /> Use This Crop
        </button>
      </div>
    </div>
  );
}
